import { Button } from '@/React/Shared/Component/Button'
import { FlashApiForm } from '@/React/Shared/Component/Flash/FlashApiForm'
import { useApiForm } from '@/React/Shared/Component/Forms/ApiForm'
import { ApiFormButton } from '@/React/Shared/Component/Forms/ApiFormButton'
import { IconNameEnum } from '@/React/Shared/Component/Icon'
import { UrlFactory } from '@/Shared/Url/UrlFactory'
import { BusinessNameGeneratorBookingPagePreview } from '@/React/FreeTool/BusinessNameGenerator/Component/BusinessNameGeneratorBookingPagePreview'
import { RoutePathFreeToolEnum } from '@/Shared/AutoGenerated/RoutePathEnum'
import { BusinessNameGeneratorContainer } from '@/React/FreeTool/BusinessNameGenerator/Component/BusinessNameGeneratorContainer'
import { FreeToolLayout } from '@/React/FreeTool/Shared/Layout/FreeToolLayout'
import {
    type BEShowBusinessNameGeneratorStepResultPreviewPagePropKeys,
    type FreeToolSharedDataRawData,
    type GeneratedBusinessName,
    type GeneratedService,
} from '@/Shared/AutoGenerated/Main'
import type { EnforceBEPagePropKeys } from '@/Shared/Helper/EnforceBEPagePropKeys'
import FreeToolSharedData from '@/FreeTool/Shared/Entity/FreeToolSharedData'

type BusinessNameGeneratorStepResultPreviewPageProps = EnforceBEPagePropKeys<
    BEShowBusinessNameGeneratorStepResultPreviewPagePropKeys,
    {
        generatedService: GeneratedService
        selectedBusinessName: GeneratedBusinessName
        freeToolSharedDataRawData: FreeToolSharedDataRawData
    }
>

export default function BusinessNameGeneratorStepResultPreviewPage({
    freeToolSharedDataRawData,
    selectedBusinessName,
    generatedService,
}: BusinessNameGeneratorStepResultPreviewPageProps) {
    const apiForm = useApiForm()
    const selectionUrl =
        new UrlFactory().makeBusinessNameGeneratorStepResultPreviewUrl(
            selectedBusinessName,
        )
    const sharedData = FreeToolSharedData.hydrate(freeToolSharedDataRawData)

    return (
        <FreeToolLayout
            appName={sharedData.appName}
            flashStatus={sharedData.flashStatus}
            headerRight={
                <span className="text-xs text-gray-500 sm:text-sm">
                    Business name generator
                </span>
            }
            footerLeft={
                <Button
                    as="a"
                    href={
                        RoutePathFreeToolEnum.BusinessNameGenerator_StepResults
                    }
                    color="light"
                    iconName={IconNameEnum.ArrowLeft}
                >
                    Back
                </Button>
            }
            footerRight={
                <FlashApiForm
                    apiForm={apiForm}
                    method="POST"
                    url={selectionUrl}
                >
                    <ApiFormButton
                        apiForm={apiForm}
                        color="primary"
                        iconName={IconNameEnum.ArrowRight}
                        iconRight
                    >
                        Choose this name
                    </ApiFormButton>
                </FlashApiForm>
            }
        >
            <BusinessNameGeneratorContainer
                eyebrow="Your booking page preview"
                title="Your business name. Your own booking page."
                description="Preview your chosen name and tagline on a booking page where clients can learn about your services and book time with you."
            >
                <div className="mx-auto max-w-3xl">
                    <BusinessNameGeneratorBookingPagePreview
                        serviceName={generatedService.name}
                        serviceDescription={generatedService.description}
                        serviceDurationInMinutes={
                            generatedService.durationInMinutes
                        }
                        businessName={selectedBusinessName.businessName}
                        businessTagline={selectedBusinessName.businessTagline}
                    />
                </div>
            </BusinessNameGeneratorContainer>
        </FreeToolLayout>
    )
}
